import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import StatCard from '../shared/StatCard';
import DateRangeFilter from '../shared/DateRangeFilter';
import RoleSummaryTab from '../shared/RoleSummaryTab';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle } from '../../styles/tokens';
import { STATUS_COLORS, STATUS_LABEL, PAYMENT_METHODS } from '@shared/constants';
import { useDataMode } from '../../context/DataModeContext';
import client from '../../api/client';

function fmt(amount) {
  if (amount === null || amount === undefined) return '—';
  return '$' + parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: '600',
      whiteSpace: 'nowrap',
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function ReviewerDashboard() {
  const navigate = useNavigate();
  const { mode } = useDataMode();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  // activeTab removed — dashboard is now a separate sidebar module

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const params = { data_mode: mode };
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        const res = await client.get('/api/receipts', { params });
        setReceipts(res.data);
      } catch (err) {
        console.error('Failed to load receipts:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [dateFrom, dateTo, mode]);

  const pendingReview = useMemo(() => receipts.filter(r => r.status === 'pending_review'), [receipts]);
  const approved = useMemo(() => receipts.filter(r => r.status === 'approved'), [receipts]);
  const paid = useMemo(() => receipts.filter(r => r.status === 'paid'), [receipts]);
  const rejected = useMemo(() => receipts.filter(r => r.status === 'rejected'), [receipts]);
  const total = receipts.length;
  const approvedAmt = approved.reduce((s, r) => {
    const items = r.items || [];
    return s + items.reduce((si, i) => {
      const a = parseFloat(i.amount) || 0;
      return si + a + a * (parseFloat(i.taxRate) || 0) / 100;
    }, 0);
  }, 0);
  const paidAmt = paid.reduce((s, r) => {
    const items = r.items || [];
    return s + items.reduce((si, i) => {
      const a = parseFloat(i.amount) || 0;
      return si + a + a * (parseFloat(i.taxRate) || 0) / 100;
    }, 0);
  }, 0);

  // Recent activity — last 10 approved/rejected by reviewer
  const recentActivity = useMemo(() => {
    return [...approved, ...rejected]
      .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))
      .slice(0, 10);
  }, [approved, rejected]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: AMBER }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: '0 0 18px' }}>
        Dashboard — Revisión Final
      </h2>

      {/* Stats */}
      <div className="stat-grid" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Total Recibos" value={total} />
        <StatCard label="Pendiente Revisión" value={pendingReview.length} highlight />
        <StatCard label="Aprobados" value={approved.length} subtitle={fmt(approvedAmt)} subtitleLarge />
        <StatCard label="Pagados" value={paid.length} subtitle={fmt(paidAmt)} subtitleLarge />
        <StatCard label="Rechazados" value={rejected.length} />
      </div>

      {/* Date filter */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center' }}>
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onChange={({ dateFrom: df, dateTo: dt }) => { setDateFrom(df); setDateTo(dt); }} />
      </div>

      {/* Pending review queue */}
      {pendingReview.length > 0 && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: NAVY, margin: '0 0 12px' }}>
            Cola de Revisión ({pendingReview.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
            {pendingReview.map(r => (
              <div
                key={r.id}
                onClick={() => navigate(`/supervisor/receipt/${r.id}`)}
                style={{
                  ...bubbleStyle,
                  padding: '14px 18px',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = bubbleStyle.boxShadow || 'none'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontWeight: 600, color: NAVY, fontSize: 14 }}>{r.vendor || 'Sin proveedor'}</span>
                    <span style={{ color: '#888', fontSize: 12, marginLeft: 12 }}>{r.employeeName}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                      {fmt((r.items || []).reduce((s, i) => { const a = parseFloat(i.amount) || 0; return s + a + a * (parseFloat(i.taxRate) || 0) / 100; }, 0))}
                    </span>
                    <StatusBadge status={r.displayStatus || r.status} />
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  {r.receiptDate ? new Date(r.receiptDate + 'T12:00:00').toLocaleDateString('es-MX') : '—'}
                  {r.paymentMethod && <span style={{ marginLeft: 10 }}>{PAYMENT_METHODS[r.paymentMethod] || r.paymentMethod}</span>}
                  {r.items?.length > 0 && <span style={{ marginLeft: 10 }}>{r.items.length} línea{r.items.length !== 1 ? 's' : ''}</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Recent activity */}
      {recentActivity.length > 0 && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: NAVY, margin: '0 0 12px' }}>
            Actividad Reciente
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {recentActivity.map(r => (
              <div
                key={r.id}
                onClick={() => navigate(`/supervisor/receipt/${r.id}`)}
                style={{
                  ...bubbleStyle,
                  padding: '10px 16px',
                  cursor: 'pointer',
                  opacity: 0.85,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: NAVY }}>{r.vendor || 'Sin proveedor'} — {r.employeeName}</span>
                  <StatusBadge status={r.status} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {pendingReview.length === 0 && recentActivity.length === 0 && (
        <div style={{ textAlign: 'center', color: '#888', padding: 40, fontSize: 14 }}>
          No hay recibos para revisar en este momento.
        </div>
      )}
    </div>
  );
}
